// /Users/donovan68/dva-frontend-vite/src/components/PersonalizationWidgetCompactV9.tsx
import React from 'react';

type Level = "not_started" | "weak" | "moderate" | "strong" | "excellent";
type StepContext = "portfolio" | "analysis" | "approval_execution";

interface PersonalizationWidgetCompactV9Props {
  onContinueClick?: () => void;
  personalizationData?: {
    strategy?: number;
    capabilities?: number;
    readiness?: number;
    docsCount?: number;
  };
  level?: Level; // optional precomputed level
  stepContext?: StepContext; // optional step context for dynamic messaging
}

// Compute qualitative level from survey data
function inferLevel(input?: {
  strategy?: number;
  capabilities?: number;
  readiness?: number;
  docsCount?: number;
}): Level {
  if (!input) return "not_started";
  const vals = [input.strategy ?? 0, input.capabilities ?? 0, input.readiness ?? 0];
  const avg = vals.reduce((a, b) => a + b, 0) / (vals.length || 1);
  const hasDocs = (input.docsCount ?? 0) > 0 ? 5 : 0;
  const score = Math.min(100, avg + hasDocs);

  if (score <= 0) return "not_started";
  if (score <= 25) return "weak";
  if (score <= 60) return "moderate";
  if (score <= 85) return "strong";
  return "excellent";
}

const LEVEL_CONFIG: Record<Level, {
  label: string;
  segments: number; // 0-5
  color: string;
  gradient: string;
  message: string;
}> = {
  not_started: {
    label: "Not Started",
    segments: 0,
    color: "#CBD5E1",
    gradient: "from-slate-300 to-slate-400",
    message: "Begin your personalization"
  },
  weak: {
    label: "Weak",
    segments: 1,
    color: "#FCA5A5",
    gradient: "from-red-300 to-red-400",
    message: "Add more details"
  },
  moderate: {
    label: "Moderate",
    segments: 3,
    color: "#FCD34D",
    gradient: "from-yellow-300 to-yellow-400",
    message: "You're making progress"
  },
  strong: {
    label: "Strong",
    segments: 4,
    color: "#34D399",
    gradient: "from-emerald-300 to-emerald-400",
    message: "Almost there!"
  },
  excellent: {
    label: "Excellent",
    segments: 5,
    color: "#6FCF97",
    gradient: "from-green-400 to-emerald-500",
    message: "Fully personalized"
  }
};

// Step-specific contextual messaging
const STEP_CONTEXT_MESSAGES: Record<StepContext, string> = {
  portfolio: "Improves portfolio scoring",
  analysis: "Refines ROI assumptions",
  approval_execution: "Aligns roadmap timing & resources"
};

// 5-Segment Circular Meter - visually engaging status indicator
const PersonalizationMeter = ({ level }: { level: Level }) => {
  const config = LEVEL_CONFIG[level];
  const activeSegments = config.segments;
  
  // 5 segments positioned in a circle
  const segments = Array.from({ length: 5 }).map((_, i) => {
    const angle = (i * 72) - 90; // 72° per segment, start at top
    const isActive = i < activeSegments;
    
    return (
      <g key={i}>
        {/* Segment arc */}
        <path
          d={describeArc(24, 24, 16, angle, angle + 60)} // 60° arc with 12° gap
          fill="none"
          stroke={isActive ? config.color : "#E6ECF2"}
          strokeWidth="4"
          strokeLinecap="round"
          className={isActive ? "drop-shadow-md" : ""}
          style={{
            transition: 'stroke 0.5s ease',
            filter: isActive ? `drop-shadow(0 0 3px ${config.color})` : 'none'
          }}
        />
      </g>
    );
  });

  return (
    <div className="relative w-12 h-12 flex items-center justify-center">
      <svg width="48" height="48" viewBox="0 0 48 48">
        {segments}
        {/* Center dot indicator */}
        <circle
          cx="24"
          cy="24"
          r="3"
          fill={activeSegments > 0 ? config.color : "#E6ECF2"}
          className={activeSegments > 0 ? "animate-pulse" : ""}
        />
      </svg>
    </div>
  );
};

// Helper function to describe an arc path
function describeArc(x: number, y: number, radius: number, startAngle: number, endAngle: number): string {
  const start = polarToCartesian(x, y, radius, endAngle);
  const end = polarToCartesian(x, y, radius, startAngle);
  const largeArcFlag = endAngle - startAngle <= 180 ? "0" : "1";
  
  return [
    "M", start.x, start.y,
    "A", radius, radius, 0, largeArcFlag, 0, end.x, end.y
  ].join(" ");
}

function polarToCartesian(centerX: number, centerY: number, radius: number, angleInDegrees: number) {
  const angleInRadians = (angleInDegrees - 90) * Math.PI / 180.0;
  return {
    x: centerX + (radius * Math.cos(angleInRadians)),
    y: centerY + (radius * Math.sin(angleInRadians))
  };
}

export function PersonalizationWidgetCompactV9({ 
  onContinueClick,
  personalizationData,
  level: providedLevel,
  stepContext
}: PersonalizationWidgetCompactV9Props) {
  const level: Level = providedLevel ?? inferLevel(personalizationData);
  const config = LEVEL_CONFIG[level];
  
  // Use step-specific message if provided, otherwise use default level message
  const displayMessage = stepContext 
    ? STEP_CONTEXT_MESSAGES[stepContext]
    : config.message;
  
  return (
    <div 
      onClick={onContinueClick}
      className="bg-gradient-to-br from-white to-[#FF6E4C]/5 rounded-xl p-4 shadow-md border-2 border-[#FF6E4C]/30 hover:shadow-lg transition-all duration-300 hover:-translate-y-0.5 hover:border-[#FF6E4C]/50 cursor-pointer flex flex-col justify-center"
      style={{ height: '100%', minHeight: '144px' }}
    >
      <div className="flex items-center justify-between mb-2">
        <h4 className="text-[#717182]" style={{ fontWeight: 600 }}>
          Personalization
        </h4>
      </div>
      
      <div className="flex items-end justify-between">
        <div>
          <div 
            style={{ fontWeight: 700, color: '#18365E' }} 
            className="text-2xl mb-1"
          >
            {config.label}
          </div>
          
          <div className="flex flex-col gap-1">
            <div className="text-xs text-[#FF6E4C]" style={{ fontWeight: 600 }}>
              Click to continue →
            </div>
            <div className="text-xs text-[#717182]" style={{ fontWeight: 500 }}>
              {displayMessage}
            </div>
          </div>
        </div>
        
        <div className="w-20 h-12 flex items-center justify-center">
          <PersonalizationMeter level={level} />
        </div>
      </div>
    </div>
  );
}